// Bot-Verwaltung (Bot-Accounts) — rendered inside Settings.
//
// Ein Bot ist ein eigener Account, der per MCP-Token im Auftrag seines Owners
// arbeitet. Wie beim persoenlichen API-Token wird der Klartext GENAU EINMAL
// angezeigt (Server speichert nur den Hash) — beim Anlegen und beim Rotieren.
// Struktur, Klassen und Modal-Mechanik folgen bewusst der ApiTokensSection;
// die wiederkehrenden Klassen-Strings liegen hier oben als Konstanten, weil
// diese Sektion deutlich mehr Buttons je Zeile hat.

import { useEffect, useState } from "preact/hooks";
import { Bot, Copy, Pencil, Plus, Power, RefreshCw, Trash2 } from "lucide-preact";
import type { BotDto, BotWithSecretDto } from "@juuplan/shared";
import { api, ApiError } from "../lib/api";
import { toastSuccess, toastError } from "../lib/toast";
import { Modal } from "./Modal";
import { INPUT_CLS } from "./NewProjectModal";

/** Serverseitiges Limit — dieselbe Zahl setzt die API mit 409 durch. */
const MAX_BOTS = 3;

const HINT = "text-xs text-[color:var(--color-on-surface-variant)]";
/** Kleiner Text-Button (Zeilen-Aktion, z. B. „Speichern"). */
const BTN_SM = "rounded px-2 py-1 text-xs font-medium hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50";
const BTN_SM_PRIMARY = `${BTN_SM} text-[color:var(--color-primary)]`;
const BTN_SM_GHOST = "rounded px-2 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]";
/** Gefuellter Bestaetigungs-Button — volle Klassennamen, sonst findet Tailwind sie nicht. */
const BTN_FILLED = "rounded px-2 py-1 text-xs font-medium text-[color:var(--color-on-primary)] bg-[color:var(--color-primary)] hover:opacity-90";
const BTN_FILLED_DANGER = "rounded px-2 py-1 text-xs font-medium text-[color:var(--color-on-error)] bg-[color:var(--color-error)] hover:opacity-90";
const BTN_MODAL_GHOST = "rounded px-3 py-1.5 text-sm text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]";
const BTN_MODAL_PRIMARY = "rounded px-3 py-1.5 text-sm font-medium text-[color:var(--color-on-primary)] bg-[color:var(--color-primary)] hover:opacity-90 disabled:opacity-50";
const ICON_BTN = "rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]";
const ICON_BTN_DANGER = "rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-error)] hover:bg-[color:var(--color-surface-container-low)]";

type Scopes = "read" | "read,write";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** „zuletzt genutzt" grob relativ — Tagesgenauigkeit reicht hier voellig. */
function fmtRelative(iso: string | null): string {
  if (!iso) return "nie";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 30) return `vor ${days} Tagen`;
  if (days < 365) return `vor ${Math.floor(days / 30)} Monaten`;
  return fmtDate(iso);
}

/** Aus dem Bot-Namen einen MCP-Server-Namen ableiten (a-z, 0-9, Bindestrich).
 *  Bewusst NICHT einfach „juuplan": ein Bot-Eintrag darf den persoenlichen
 *  MCP-Server des Users in `~/.claude.json` nicht ueberschreiben. */
function mcpServerName(botName: string): string {
  const slug = botName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug ? `juuplan-${slug}` : "juuplan-bot";
}

/** Der fertige Einbinde-Befehl fuer Claude Code, inklusive Bot-Token.
 *
 *  scope "local" (Default von `claude mcp add`) bindet nur im aktuellen
 *  Projektverzeichnis ein, "user" in allen Projekten — beide landen in
 *  `~/.claude.json`, NICHT in einer versionierten Datei. Absichtlich KEIN
 *  "project"-Scope: der schreibt in `.mcp.json` im Repo-Root, das i. d. R.
 *  eingecheckt wird — damit landete das Bot-Token in der Git-Historie. */
function mcpAddCommand(scope: "local" | "user", botName: string, token: string): string {
  return `claude mcp add --transport http ${mcpServerName(botName)} ${window.location.origin}/api/mcp --header "Authorization: Bearer ${token}" --scope ${scope}`;
}

/** Code-Block mit Kopier-Button (rechts oben). */
function CommandBlock({ command }: { command: string }): preact.JSX.Element {
  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(command);
      toastSuccess("Befehl kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };
  return (
    <div class="relative">
      <code class="block whitespace-pre-wrap break-all rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 pr-9 text-micro text-[color:var(--color-on-surface)]">
        {command}
      </code>
      <button
        type="button"
        aria-label="Befehl kopieren"
        title="Kopieren"
        onClick={() => void copy()}
        class={`absolute top-1 right-1 ${ICON_BTN}`}
      >
        <Copy size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

interface RowActionProps { label: string; icon: preact.JSX.Element; danger?: boolean; onClick: () => void }

/** Icon-Aktion am rechten Rand einer Bot-Zeile. */
function RowAction({ label, icon, danger, onClick }: RowActionProps): preact.JSX.Element {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} class={danger ? ICON_BTN_DANGER : ICON_BTN}>
      {icon}
    </button>
  );
}

interface ConfirmRowProps { hint: string; confirmLabel: string; danger?: boolean; onConfirm: () => void; onCancel: () => void }

/** Inline-Bestaetigung, die die Aktions-Icons einer Zeile ersetzt. */
function ConfirmRow({ hint, confirmLabel, danger, onConfirm, onCancel }: ConfirmRowProps): preact.JSX.Element {
  return (
    <div class="flex items-center gap-2">
      <span class={HINT}>{hint}</span>
      <button type="button" onClick={onConfirm} class={danger ? BTN_FILLED_DANGER : BTN_FILLED}>
        {confirmLabel}
      </button>
      <button type="button" onClick={onCancel} class={BTN_SM_GHOST}>
        Abbrechen
      </button>
    </div>
  );
}

/** Fehler der Bot-API in eine verstaendliche deutsche Meldung uebersetzen.
 *  409 ist doppelt belegt (Namenskollision vs. Limit), deshalb entscheidet der
 *  Kontext: beim Anlegen kann beides auftreten, beim Umbenennen nur der Name. */
function botErrorText(err: unknown, ctx: "create" | "rename"): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return "Ungültiger Name — 2 bis 40 Zeichen, kein @-Zeichen.";
    if (err.status === 409) {
      if (ctx === "create" && err.message.toLowerCase().includes("limit")) {
        return `Limit erreicht — mehr als ${MAX_BOTS} aktive Bots sind nicht möglich.`;
      }
      return "Diesen Namen gibt es schon.";
    }
    if (err.message) return err.message;
  }
  return "Aktion fehlgeschlagen — bitte erneut versuchen.";
}

export function BotsSection(): preact.JSX.Element {
  const [items, setItems] = useState<BotDto[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Scopes>("read,write");
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  /** Frisch erzeugtes Geheimnis — nur sichtbar, solange dieses State lebt. */
  const [secret, setSecret] = useState<BotWithSecretDto | null>(null);
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [confirmRotate, setConfirmRotate] = useState<number | null>(null);
  const [confirmDel, setConfirmDel] = useState<number | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Leere Liste und Ladefehler sahen identisch aus („Noch kein Bot angelegt"):
  // api() toastet nur Mutationen (MUTATION_METHODS in lib/api.ts), GETs nicht.
  const onLoadFailed = (): void => { setItems([]); setLoadError(true); toastError("Bots konnten nicht geladen werden."); };
  const load = (): void => { setLoadError(false); void api<BotDto[]>("/api/me/bots").then(setItems).catch(onLoadFailed); };
  useEffect(load, []);

  const activeCount = (items ?? []).filter((b) => b.is_active).length;
  const limitReached = activeCount >= MAX_BOTS;

  const create = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setCreateError(null);
    try {
      const body = { name: trimmed, scopes };
      const result = await api<BotWithSecretDto>("/api/me/bots", { method: "POST", body, silentError: true });
      setSecret(result);
      setShowCreate(false);
      setName("");
      setScopes("read,write");
      load();
    } catch (err) {
      setCreateError(botErrorText(err, "create"));
    } finally {
      setBusy(false);
    }
  };

  const rename = async (id: number): Promise<void> => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    try {
      await api<BotDto>(`/api/me/bots/${id}`, { method: "PATCH", body: { name: trimmed }, silentError: true });
      toastSuccess("Bot umbenannt.");
      setRenameId(null);
      load();
    } catch (err) {
      toastError(botErrorText(err, "rename"));
    }
  };

  const toggleActive = async (bot: BotDto): Promise<void> => {
    try {
      const body = { is_active: !bot.is_active };
      await api<BotDto>(`/api/me/bots/${bot.id}`, { method: "PATCH", body, silentError: true });
      toastSuccess(bot.is_active ? "Bot deaktiviert." : "Bot aktiviert.");
      load();
    } catch (err) {
      toastError(
        err instanceof ApiError && err.status === 409
          ? `Limit erreicht — höchstens ${MAX_BOTS} aktive Bots.`
          : "Aktion fehlgeschlagen — bitte erneut versuchen."
      );
    }
  };

  const rotate = async (id: number): Promise<void> => {
    try {
      const result = await api<BotWithSecretDto>(`/api/me/bots/${id}/token`, { method: "POST", body: {} });
      setConfirmRotate(null);
      setSecret(result);
      load();
    } catch {
      // api wrapper already toasted
    }
  };

  const remove = async (id: number): Promise<void> => {
    try {
      await api<void>(`/api/me/bots/${id}`, { method: "DELETE" });
      toastSuccess("Bot entfernt — sein Token ist sofort ungültig.");
      setConfirmDel(null);
      load();
    } catch {
      // api wrapper already toasted
    }
  };

  const copyToken = async (token: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(token);
      toastSuccess("Token kopiert.");
    } catch {
      toastError("Kopieren fehlgeschlagen — bitte manuell markieren.");
    }
  };

  return (
    <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
      <div class="flex items-center justify-between px-3 py-2 border-b border-[color:var(--color-outline-variant)]">
        <span class="flex items-center gap-2 text-sm font-medium text-[color:var(--color-on-surface)]">
          <Bot size={16} aria-hidden="true" />
          Bots
          <span class={`font-normal ${HINT}`}>
            {activeCount} von {MAX_BOTS} genutzt
          </span>
        </span>
        <button
          type="button"
          disabled={limitReached}
          title={limitReached ? `Höchstens ${MAX_BOTS} aktive Bots.` : undefined}
          onClick={() => {
            setCreateError(null);
            setShowCreate(true);
          }}
          class={`flex items-center gap-1 ${BTN_SM_PRIMARY} disabled:hover:bg-transparent`}
        >
          <Plus size={14} aria-hidden="true" />
          Bot anlegen
        </button>
      </div>

      {/* Vorspann: was ein Bot ist und was er darf. */}
      <div class="border-b border-[color:var(--color-outline-variant)] px-3 py-2.5">
        <p class={HINT}>
          Ein Bot ist ein eigener Account, der über ein MCP-Token in deinem
          Auftrag arbeitet und dabei genau deine Rechte erbt — er sieht und
          ändert nichts, was du nicht auch selbst dürftest. In Aufgaben-
          Kommentaren sprichst du ihn per <code>@Name</code> an, als Zuständiger
          ist er ebenso wählbar wie eine Person. Alles, was ein Bot anlegt oder
          schreibt, ist überall sichtbar als Bot gekennzeichnet.
        </p>
      </div>

      {items === null ? (
        <p class={`p-3 ${HINT}`}>Lädt…</p>
      ) : loadError ? (
        <p class="p-3 text-xs text-[color:var(--color-error)]">
          Bots konnten nicht geladen werden.{" "}
          <button type="button" onClick={load} class="underline">Erneut laden</button></p>
      ) : items.length === 0 ? (
        <p class={`p-3 italic ${HINT}`}>
          Noch kein Bot angelegt. Erstelle einen, um z. B. Claude Code als
          eigenständigen Mitarbeiter in deinen Projekten mitarbeiten zu lassen.
        </p>
      ) : (
        <ul class="divide-y divide-[color:var(--color-outline-variant)]">
          {items.map((b) => (
            <li key={b.id} class="flex items-center gap-3 px-3 py-2 text-sm">
              <div class="flex-1 min-w-0">
                {renameId === b.id ? (
                  <div class="flex items-center gap-2">
                    <input
                      type="text"
                      value={renameValue}
                      onInput={(e) => setRenameValue((e.target as HTMLInputElement).value)}
                      maxLength={40}
                      class={INPUT_CLS}
                    />
                    <button
                      type="button"
                      onClick={() => void rename(b.id)}
                      disabled={!renameValue.trim()}
                      class={BTN_SM_PRIMARY}
                    >
                      Speichern
                    </button>
                    <button type="button" onClick={() => setRenameId(null)} class={BTN_SM_GHOST}>
                      Abbrechen
                    </button>
                  </div>
                ) : (
                  <>
                    <span class="block truncate text-[color:var(--color-on-surface)]">
                      {b.name}
                      {!b.is_active && (
                        <span class="ml-2 rounded bg-[color:var(--color-surface-container)] px-1.5 py-0.5 text-label text-[color:var(--color-on-surface-variant)]">
                          deaktiviert
                        </span>
                      )}
                    </span>
                    <span class={`block ${HINT}`}>
                      {b.token ? (
                        <>
                          <code>{b.token.prefix}…</code>
                          {" · "}
                          {b.token.scopes === "read" ? "Nur lesen" : "Lesen + Schreiben"}
                          {" · zuletzt genutzt: "}
                          {fmtRelative(b.token.last_used_at)}
                        </>
                      ) : (
                        "kein Token — bitte neu erzeugen"
                      )}
                      {" · angelegt: "}
                      {fmtDate(b.created_at)}
                    </span>
                  </>
                )}
              </div>

              {confirmRotate === b.id ? (
                <ConfirmRow
                  hint="Altes Token wird sofort ungültig."
                  confirmLabel="Neu erzeugen"
                  onConfirm={() => void rotate(b.id)}
                  onCancel={() => setConfirmRotate(null)}
                />
              ) : confirmDel === b.id ? (
                <ConfirmRow
                  hint="Bot wird deaktiviert, Token widerrufen."
                  confirmLabel="Entfernen"
                  danger
                  onConfirm={() => void remove(b.id)}
                  onCancel={() => setConfirmDel(null)}
                />
              ) : renameId === b.id ? null : (
                <div class="flex items-center gap-1">
                  <RowAction
                    label={`Bot ${b.name} umbenennen`}
                    icon={<Pencil size={16} aria-hidden="true" />}
                    onClick={() => {
                      setRenameValue(b.name);
                      setRenameId(b.id);
                    }}
                  />
                  <RowAction
                    label={`Token von ${b.name} neu erzeugen`}
                    icon={<RefreshCw size={16} aria-hidden="true" />}
                    onClick={() => setConfirmRotate(b.id)}
                  />
                  <RowAction
                    label={b.is_active ? `Bot ${b.name} deaktivieren` : `Bot ${b.name} aktivieren`}
                    icon={<Power size={16} aria-hidden="true" />}
                    onClick={() => void toggleActive(b)}
                  />
                  <RowAction
                    label={`Bot ${b.name} entfernen`}
                    icon={<Trash2 size={16} aria-hidden="true" />}
                    danger
                    onClick={() => setConfirmDel(b.id)}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {showCreate && (
        <Modal onClose={() => setShowCreate(false)} title="Neuen Bot anlegen">
          <div class="flex flex-col gap-3">
            <label class="flex flex-col gap-1 text-sm text-[color:var(--color-on-surface)]">
              Name
              <input
                type="text"
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                placeholder="z. B. Buildbot"
                maxLength={40}
                class={INPUT_CLS}
              />
            </label>
            <p class={`-mt-1 ${HINT}`}>
              2 bis 40 Zeichen, kein @-Zeichen — unter diesem Namen erscheint der
              Bot überall und wird per <code>@Name</code> erwähnt.
            </p>
            <label class="flex flex-col gap-1 text-sm text-[color:var(--color-on-surface)]">
              Berechtigung
              <select
                value={scopes}
                onChange={(e) => setScopes((e.target as HTMLSelectElement).value as Scopes)}
                class={INPUT_CLS}
              >
                <option value="read,write">Lesen + Schreiben</option>
                <option value="read">Nur lesen</option>
              </select>
            </label>
            <p class={HINT}>
              {scopes === "read,write" ? (
                <>
                  <strong>Lesen + Schreiben:</strong> der Bot kann in deinem
                  Auftrag kommentieren, Aufgaben anlegen und ändern — immer nur
                  dort, wo du selbst Schreibrechte hast.
                </>
              ) : (
                <>
                  <strong>Nur lesen:</strong> der Bot kann nichts anlegen und auf
                  eine Erwähnung nicht antworten. Wer per <code>@Name</code>
                  {" "}angesprochen werden soll, braucht Lesen + Schreiben.
                </>
              )}
            </p>
            {createError && <p class="text-xs text-[color:var(--color-error)]">{createError}</p>}
            <div class="flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)} class={BTN_MODAL_GHOST}>
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void create()}
                disabled={busy || !name.trim()}
                class={BTN_MODAL_PRIMARY}
              >
                {busy ? "Legt an…" : "Anlegen"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {secret && (
        <Modal onClose={() => setSecret(null)} title="Bot-Token">
          <div class="flex flex-col gap-3">
            <p class="text-sm text-[color:var(--color-on-surface)]">
              Token für <strong>{secret.name}</strong> erzeugt. Kopiere es jetzt
              — es wird <strong>nie wieder angezeigt</strong>.
            </p>
            <div class="flex items-center gap-2">
              <code class="flex-1 min-w-0 truncate rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] px-2 py-1.5 text-xs">
                {secret.token_plaintext}
              </code>
              <button
                type="button"
                onClick={() => void copyToken(secret.token_plaintext)}
                class={`flex items-center gap-1 py-1.5 ${BTN_SM_PRIMARY}`}
              >
                <Copy size={14} aria-hidden="true" />
                Kopieren
              </button>
            </div>
            <p class={HINT}>
              Einbinden in Claude Code — der Befehl enthält das Token, einfach
              kopieren und im Terminal ausführen:
            </p>
            <p class={HINT}>Nur für dieses Projekt:</p>
            <CommandBlock command={mcpAddCommand("local", secret.name, secret.token_plaintext)} />
            <p class={HINT}>Für alle Projekte (user-weit):</p>
            <CommandBlock command={mcpAddCommand("user", secret.name, secret.token_plaintext)} />
            <button
              type="button"
              onClick={() => setSecret(null)}
              class="self-end rounded px-3 py-1.5 text-sm font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)]"
            >
              Fertig
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
